import {
  BASE_LOCALE,
  LOCALES,
  SITE_ORIGIN,
  locales,
  markets,
  type Locale,
  type Market,
} from "./locale-registry";

export { BASE_LOCALE, locales, markets };
export type { Locale, Market };

export type SiteContext = {
  locale: Locale;
  market: Market;
  origin: string;
  pathPrefix: string;
};

export const sites = Object.fromEntries(
  LOCALES.map((definition) => [
    definition.code,
    {
      locale: definition.code,
      market: definition.market,
      origin: SITE_ORIGIN,
      pathPrefix: definition.pathPrefix,
    },
  ]),
) as Record<Locale, SiteContext>;

export function isLocale(value: string | null | undefined): value is Locale {
  return locales.includes(value as Locale);
}

// Every incoming URL prefix that maps to a locale (its public prefix plus any
// aliases), derived from the registry. The base locale's empty prefix is omitted
// because it is the unprefixed default. Example: "/ua" & "/uk" -> uk, "/pl" -> pl.
const localePrefixes: ReadonlyArray<readonly [string, Locale]> = LOCALES.flatMap((definition) =>
  [definition.pathPrefix, ...definition.aliases]
    .filter((prefix) => prefix.length > 0)
    .map((prefix) => [prefix, definition.code] as const),
);

export function resolveSiteContext(input: string | URL): SiteContext {
  if (typeof window !== "undefined") {
    input = window.location.pathname;
  }

  const url = typeof input === "string" ? new URL(input, SITE_ORIGIN) : input;
  const override = url.searchParams.get("site");

  if (isLocale(override)) return sites[override];

  for (const [prefix, locale] of localePrefixes) {
    if (url.pathname === prefix || url.pathname.startsWith(`${prefix}/`)) return sites[locale];
  }

  return sites[BASE_LOCALE];
}

export function stripPublicPrefix(pathname: string): string {
  for (const [prefix] of localePrefixes) {
    if (pathname === prefix) return "/";
    if (pathname.startsWith(`${prefix}/`)) return pathname.slice(prefix.length);
  }
  return pathname;
}

function normalizeComparablePath(value: string): string {
  try {
    const pathname = new URL(value, sites.en.origin).pathname.replace(/\/+$/, "") || "/";
    return stripPublicPrefix(pathname);
  } catch {
    return stripPublicPrefix(value);
  }
}

export function isPublicPathActive(current: string, target: string, homeTarget: string): boolean {
  const here = normalizeComparablePath(current);
  const destination = normalizeComparablePath(target);
  const home = normalizeComparablePath(homeTarget);

  if (destination === home) return here === home;
  return here === destination || here.startsWith(`${destination}/`);
}

export function addPublicPrefix(pathname: string, site: SiteContext): string {
  const normalized = pathname.startsWith("/") ? pathname : `/${pathname}`;
  if (!site.pathPrefix) return normalized;
  return normalized === "/" ? site.pathPrefix : `${site.pathPrefix}${normalized}`;
}

export function isLocalHostname(hostname: string): boolean {
  return ["localhost", "127.0.0.1", "::1", "0.0.0.0"].includes(hostname);
}

export function localizeUrl(pathname: string, target: Locale, currentUrl: string | URL): string {
  const url = typeof currentUrl === "string" ? new URL(currentUrl, sites.en.origin) : currentUrl;
  const targetSite = sites[target];
  const destination = new URL(pathname, url.origin);
  const publicPath = addPublicPrefix(stripPublicPrefix(destination.pathname), targetSite);

  const localUrl = new URL(publicPath, url.origin);
  localUrl.search = destination.search;
  localUrl.hash = destination.hash;
  return `${localUrl.pathname}${localUrl.search}${localUrl.hash}`;
}

export function siteHintForRequest(url: URL): Locale | undefined {
  if (isLocale(url.searchParams.get("site"))) return undefined;
  const site = resolveSiteContext(url);
  return site.locale === "en" ? undefined : site.locale;
}

export function withSiteHint(request: Request): Request {
  const url = new URL(request.url);
  const hint = siteHintForRequest(url);
  if (!hint) return request;
  url.searchParams.set("site", hint);
  return new Request(url, request);
}
